import { useCallback, useEffect, useRef, useState } from 'react';
import supabase from '@/supabase';
import type { Bot, BotMessage, BotConversation, BotMediaReference } from '@/types/bot';
import { useAdminRuntimeConfig } from '@/hooks/use-admin-runtime-config';
import { generateCorrelationId, getRepoHeaders } from '@/lib/runtime-monitoring';

const AI_GATEWAY_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-gateway`;

interface UseBotChatOptions {
  botId: string;
  userId: string;
  enabled?: boolean;
}

interface UseBotChatResult {
  bot: Bot | null;
  conversation: BotConversation | null;
  messages: BotMessage[];
  isLoading: boolean;
  isSending: boolean;
  isStreaming: boolean;
  streamingContent: string;
  error: string | null;
  sendMessage: (content: string) => Promise<{ success: boolean; error?: string }>;
  remainingMessages: number | null;
}

interface GatewayChatResponse {
  success: boolean;
  data?: {
    content?: string;
    choices?: Array<{
      message?: {
        content?: string;
      };
    }>;
  };
  error?: {
    message?: string;
  };
  metadata?: {
    remaining_messages?: number | null;
  };
}

interface GatewayBotContext {
  name: string;
  description: string;
  system_prompt: string;
  tone: string;
  personality_traits: string[];
  knowledge_base: string[];
  greeting_messages: string[];
}

export function useBotChat({
  botId,
  userId,
  enabled = true,
}: UseBotChatOptions): UseBotChatResult {
  const { config: adminRuntimeConfig } = useAdminRuntimeConfig();
  const [bot, setBot] = useState<Bot | null>(null);
  const [conversation, setConversation] = useState<BotConversation | null>(null);
  const [messages, setMessages] = useState<BotMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [remainingMessages, setRemainingMessages] = useState<number | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch bot and existing conversation/messages
  useEffect(() => {
    if (!enabled || !botId || !userId) {
      setIsLoading(false);
      return;
    }

    const fetchBotAndConversation = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch bot details
        const { data: botData, error: botError } = await supabase
          .from('bots')
          .select('*')
          .eq('id', botId)
          .single();

        if (botError) {
          throw new Error('Bot not found');
        }

        setBot(botData as Bot);

        // Check for existing conversation
        const { data: existingConversation, error: convError } = await supabase
          .from('bot_conversations')
          .select('*')
          .eq('bot_id', botId)
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (convError) {
          console.error('Error fetching conversation:', convError);
        }

        if (existingConversation) {
          setConversation(existingConversation);

          // Fetch existing messages with media references
          const { data: existingMessages, error: messagesError } = await supabase
            .from('bot_messages')
            .select(`
              *,
              media_reference:bot_media_references(*)
            `)
            .eq('conversation_id', existingConversation.id)
            .order('created_at', { ascending: true });

          if (messagesError) {
            console.error('Error fetching messages:', messagesError);
          } else {
            // Normalize media_reference from array to single object
            const normalizedMessages = (existingMessages || []).map((msg) => ({
              ...msg,
              media_reference: Array.isArray(msg.media_reference)
                ? msg.media_reference[0] ?? null
                : msg.media_reference ?? null,
            })) as BotMessage[];
            setMessages(normalizedMessages);
          }
        }
      } catch (err) {
        console.error('Error in useBotChat:', err);
        setError(err instanceof Error ? err.message : 'Failed to load chat');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBotAndConversation();
  }, [botId, userId, enabled]);

  const sendMessage = useCallback(
    async (content: string): Promise<{ success: boolean; error?: string }> => {
      if (!bot || !userId || !content.trim()) {
        return { success: false, error: 'Invalid message or state' };
      }

      // Cancel any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setIsSending(true);
      setIsStreaming(false);
      setStreamingContent('');
      setError(null);

      try {
        // Create conversation if it doesn't exist
        let conversationId: string = conversation?.id || '';

        if (!conversationId) {
          const { data: newConversation, error: createError } = await supabase
            .from('bot_conversations')
            .insert({
              bot_id: botId,
              user_id: userId,
            })
            .select()
            .single();

          if (createError || !newConversation) {
            throw new Error('Failed to create conversation');
          }

          setConversation(newConversation);
          conversationId = newConversation.id;
        }

        // Create optimistic user message
        const userMessage: BotMessage = {
          id: crypto.randomUUID(),
          conversation_id: conversationId,
          role: 'user',
          content: content.trim(),
          media_reference_id: null,
          created_at: new Date().toISOString(),
        };

        // Add user message optimistically
        setMessages((prev) => [...prev, userMessage]);

        // Insert user message into database
        const { error: insertError } = await supabase
          .from('bot_messages')
          .insert({
            id: userMessage.id,
            conversation_id: conversationId,
            role: 'user',
            content: userMessage.content,
          });

        if (insertError) {
          console.error('Error inserting user message:', insertError);
        }

        // Get session for auth token
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        if (!accessToken) {
          throw new Error('Not authenticated');
        }

        // Build conversation history for context
        const conversationHistory = messages.map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));

        // Add the new user message
        conversationHistory.push({
          role: 'user' as const,
          content: content.trim(),
        });

        // Call AI gateway using the verified non-stream chat contract.
        const correlationId = generateCorrelationId();
        const response = await fetch(AI_GATEWAY_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
            ...getRepoHeaders(correlationId),
          },
          body: JSON.stringify({
            bot_id: botId,
            request_type: 'chat',
            stream: false,
            bot_context: {
              name: bot.name,
              description: bot.persona.description,
              system_prompt: bot.persona.system_prompt,
              tone: bot.persona.tone,
              personality_traits: bot.persona.personality_traits,
              knowledge_base: bot.persona.knowledge_base,
              greeting_messages: bot.persona.greeting_messages,
            } satisfies GatewayBotContext,
            provider_options: {
              chat_generation_url: adminRuntimeConfig.chat_generation_url,
              media_generation_url: adminRuntimeConfig.media_generation_url,
              media_location_url: adminRuntimeConfig.media_location_url,
            },
            messages: conversationHistory,
          }),
          signal: abortControllerRef.current.signal,
        });

        const gatewayResponse = (await response.json().catch(() => ({}))) as GatewayChatResponse;

        if (!response.ok) {
          const errorMessage =
            gatewayResponse.error?.message || `Request failed: ${response.status}`;

          if (response.status === 403) {
            throw new Error('You need to upgrade your subscription to chat with this bot');
          }
          if (response.status === 429) {
            throw new Error('Daily message limit reached. Upgrade to send more messages');
          }

          throw new Error(errorMessage);
        }

        if (!gatewayResponse.success) {
          throw new Error(gatewayResponse.error?.message || 'Failed to get bot response');
        }

        const assistantContent =
          gatewayResponse.data?.content ||
          gatewayResponse.data?.choices?.[0]?.message?.content ||
          '';

        if (!assistantContent.trim()) {
          throw new Error('Bot returned an empty response');
        }

        if (gatewayResponse.metadata?.remaining_messages !== undefined) {
          setRemainingMessages(gatewayResponse.metadata.remaining_messages);
        }

        const assistantMessage: BotMessage = {
          id: crypto.randomUUID(),
          conversation_id: conversationId,
          role: 'assistant',
          content: assistantContent,
          media_reference_id: null,
          media_reference: null as BotMediaReference | null,
          created_at: new Date().toISOString(),
        };

        // Add assistant message to state
        setMessages((prev) => [...prev, assistantMessage]);
        setStreamingContent('');
        setIsStreaming(false);

        // Insert assistant message into database
        await supabase.from('bot_messages').insert({
          id: assistantMessage.id,
          conversation_id: conversationId,
          role: 'assistant',
          content: assistantMessage.content,
          media_reference_id: null,
        });

        return { success: true };
      } catch (err) {
        // Handle abort
        if (err instanceof Error && err.name === 'AbortError') {
          return { success: false, error: 'Request cancelled' };
        }

        console.error('Error sending message:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
        setError(errorMessage);

        // Remove optimistic message on error
        setMessages((prev) => prev.slice(0, -1));

        return { success: false, error: errorMessage };
      } finally {
        setIsSending(false);
        setIsStreaming(false);
        abortControllerRef.current = null;
      }
    },
    [adminRuntimeConfig.chat_generation_url, adminRuntimeConfig.media_generation_url, adminRuntimeConfig.media_location_url, bot, userId, botId, conversation, messages]
  );

  return {
    bot,
    conversation,
    messages,
    isLoading,
    isSending,
    isStreaming,
    streamingContent,
    error,
    sendMessage,
    remainingMessages,
  };
}
